import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

// Ensure this matches your backend URL logic
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // 1. INITIALIZE STATE DIRECTLY FROM STORAGE
  // We do this immediately so there is no "loading" state that kicks you out.
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("tau_user");
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });

  const [token, setToken] = useState(() => localStorage.getItem("tau_token"));
  const [isLoading, setIsLoading] = useState(false);

  // 2. AXIOS INTERCEPTOR (Attaches token to every request)
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const storedToken = localStorage.getItem("tau_token");
        if (storedToken) {
          config.headers.Authorization = `Bearer ${storedToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor (Logs out if server says 401 Unauthorized)
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // 3. LOGIN FUNCTION (Updated to match Backend response)
  const login = async (username, password) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { username, password });
      
      // FIX: Backend returns 'token', not 'access_token'
      const { token: accessToken, user: userData } = response.data;
      
      if (!userData || !accessToken) {
        throw new Error("Invalid response from server");
      }

      // Save to storage
      localStorage.setItem("tau_token", accessToken);
      localStorage.setItem("tau_user", JSON.stringify(userData));

      // Update State
      setToken(accessToken);
      setUser(userData);
      
      return userData;
    } catch (error) {
      console.error("Login Error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 4. LOGOUT FUNCTION
  const logout = () => {
    localStorage.removeItem("tau_token");
    localStorage.removeItem("tau_user");
    setToken(null);
    setUser(null);
  };

  // Role Helpers
  const isAdmin = () => user?.role === "admin";
  const isOperator = () => user?.role === "admin" || user?.role === "operator";
  const isViewer = () => !!user;

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!token && !!user,
    login,
    logout,
    isAdmin,
    isOperator,
    isViewer,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}